import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, combineLatest, switchMap, map } from 'rxjs';
import { TranslatePageModuleName, TranslatePagePath } from './translate-page';
import { getManifest } from './manifestLoader';

function getTranslatePageModuleName(translatePagePath: string): TranslatePageModuleName {
  const pathKey = Object.keys(TranslatePagePath).find(
    key => TranslatePagePath[key] === translatePagePath,
  );
  return TranslatePageModuleName[pathKey];
}

export function customHttpLoaderFactory(http: HttpClient, moduleName: string | string[]): TranslateLoader {
  return new UniversalTranslateLoader(http, moduleName);
}

export class UniversalTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private moduleName?: string | string[],
    private prefix: string = 'i18n',
    private suffix: string = '.json',
  ) {}

  getTranslation(lang: string): Observable<any> {
    if (!this.moduleName) {
      const defaultPath = `${this.prefix}/${lang}${this.suffix}`;
      return this.loadSingleTranslation(defaultPath);
    }

    const modules = Array.isArray(this.moduleName) ? this.moduleName : [this.moduleName];

    return combineLatest(
      modules.map(moduleName => this.loadTranslation(lang, moduleName)),
    ).pipe(
      map(translationsArray =>
        translationsArray.reduce((combined, current) => ({ ...combined, ...current }), {}),
      ),
    );
  }

  private loadTranslation(lang: string, moduleName: string): Observable<any> {
    const modulePath = `${this.prefix}/${moduleName}/${lang}${this.suffix}`;

    return getManifest(this.http).pipe(
      map(manifest => manifest[modulePath] || `./${modulePath}`),
      switchMap(filePath =>
        this.http.get(filePath).pipe(
          map(translations => ({
            [getTranslatePageModuleName(moduleName)]: translations,
          })),
        ),
      ),
    );
  }

  private loadSingleTranslation(path: string): Observable<any> {
    return getManifest(this.http).pipe(
      map(manifest => manifest[path] || `./${path}`),
      switchMap(filePath => this.http.get(filePath)),
    );
  }
}
